import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@kognitos/lattice';
import {
  useUserPreferencesStore,
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
} from '@/shared/stores';
import {
  useAddWorkspaceBookConnectionTriggerInstance,
  useSetAutomationsForTriggerInstance,
  getRetrieveWorkspaceTriggerInstancesQueryKey,
} from '@/generated/hooks/bdk/v2/services/library';
import {
  useCreateAutomationTrigger,
  getListAutomationTriggersQueryKey,
} from '@/generated/hooks/grimoire/v1/automation_phase';
import {
  CreateAutomationTriggerStage,
  V1PhaseType,
} from '@/generated/hooks/grimoire/models';
import { captureException } from '@/modules/sentry';
import type {
  IUseCreateTriggerProps,
  IUseCreateTriggerReturn,
  ICreateTriggerData,
  TSentryTags,
} from './types';

/**
 * Generic hook for creating triggers (webhook, email, etc.).
 *
 * Encapsulates the common logic for creating a trigger by:
 * 1. Creating a trigger instance in BDK
 * 2. Setting automations for the trigger instance in BDK
 * 3. Creating an automation trigger in Grimoire with input mappings
 *
 * @param props - Hook props
 * @returns Object containing createTrigger function and isLoading state
 *
 * @example
 * // For webhook trigger
 * const { createTrigger, isLoading } = useCreateTrigger({
 *   automationId: 'automation-123',
 *   onSuccess: () => closeDialog(),
 *   toastMessages: {
 *     success: t('webhookDialog.toast.success'),
 *     error: t('webhookDialog.toast.error'),
 *     errorNoTriggerId: t('webhookDialog.toast.errorNoTriggerId'),
 *   },
 * });
 *
 * @example
 * // For email trigger
 * const { createTrigger, isLoading } = useCreateTrigger({
 *   automationId: 'automation-123',
 *   onSuccess: () => closeDialog(),
 *   toastMessages: {
 *     success: t('emailDialog.toast.success'),
 *     error: t('emailDialog.toast.error'),
 *     errorNoTriggerId: t('emailDialog.toast.error'),
 *   },
 * });
 */
export function useCreateTrigger({
  automationId,
  isDraft = true,
  onSuccess,
  toastMessages,
  sentryTags,
}: IUseCreateTriggerProps): IUseCreateTriggerReturn {
  // Get organization and workspace IDs for API calls
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  // Query client for cache invalidation
  const queryClient = useQueryClient();

  // Mutation to create trigger instance in BDK
  const {
    mutateAsync: addTriggerInstance,
    isPending: isAddingTriggerInstance,
  } = useAddWorkspaceBookConnectionTriggerInstance();

  // Mutation to set automations for trigger instance in BDK
  const {
    mutateAsync: setAutomationsForTriggerInstance,
    isPending: isSettingAutomations,
  } = useSetAutomationsForTriggerInstance();

  // Mutation to create automation trigger in Grimoire
  const {
    mutateAsync: createAutomationTrigger,
    isPending: isCreatingAutomationTrigger,
  } = useCreateAutomationTrigger();

  /**
   * Creates a trigger by:
   * 1. Creating trigger instance in BDK
   * 2. Setting automations for the trigger instance
   * 3. Creating automation trigger in Grimoire
   *
   * @param data - Trigger creation data
   */
  const createTrigger = useCallback(
    async (data: ICreateTriggerData) => {
      const {
        bookName,
        bookVersion,
        connectionId,
        triggerId,
        configuration,
        filterExpression,
        inputMappings,
      } = data;

      // Validate required fields
      if (!bookName || !bookVersion || !connectionId || !triggerId) {
        return;
      }

      try {
        // Step 1: Create trigger instance in BDK
        const bdkResponse = await addTriggerInstance({
          organizationId,
          workspaceId,
          bookName,
          bookVersion,
          connectionId,
          data: {
            trigger_id: triggerId,
            configuration,
            filter_expression: filterExpression,
          },
        });

        // Get the trigger instance ID from BDK response
        const triggerInstanceId = bdkResponse.data.trigger_instance?.id;

        if (!triggerInstanceId) {
          toast.error(toastMessages.errorNoTriggerId);
          return;
        }

        // Step 2: Set automations for the trigger instance in BDK
        await setAutomationsForTriggerInstance({
          organizationId,
          workspaceId,
          triggerInstanceId,
          data: {
            automation_ids: [automationId],
            connection_id: connectionId,
          },
        });

        // Step 3: Create automation trigger in Grimoire with the mapping.
        // The stage query param scopes the trigger to the draft or published
        // version of the automation.
        await createAutomationTrigger({
          organization: organizationId,
          workspace: workspaceId,
          automation: automationId,
          data: {
            trigger_id: triggerInstanceId,
            input_mapping: inputMappings,
            phase_type: V1PhaseType.PHASE_TYPE_PRE,
          },
          params: {
            stage: isDraft
              ? CreateAutomationTriggerStage.AUTOMATION_STAGE_DRAFT
              : CreateAutomationTriggerStage.AUTOMATION_STAGE_PUBLISHED,
          },
        });

        // Invalidate both Grimoire and BDK trigger queries to refresh the list
        queryClient.invalidateQueries({
          queryKey: getListAutomationTriggersQueryKey(
            organizationId,
            workspaceId,
            automationId,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: getRetrieveWorkspaceTriggerInstancesQueryKey(
            organizationId,
            workspaceId,
          ),
        });

        // Show success toast and call onSuccess callback
        toast.success(toastMessages.success);
        onSuccess?.();
      } catch (error) {
        const defaultTags: TSentryTags = {
          component: 'useCreateTrigger',
          action: 'createTrigger',
        };
        captureException(error as Error, {
          tags: sentryTags ?? defaultTags,
        });
        toast.error(toastMessages.error);
      }
    },
    [
      organizationId,
      workspaceId,
      automationId,
      isDraft,
      addTriggerInstance,
      setAutomationsForTriggerInstance,
      createAutomationTrigger,
      queryClient,
      toastMessages,
      sentryTags,
      onSuccess,
    ],
  );

  return {
    createTrigger,
    isLoading:
      isAddingTriggerInstance ||
      isSettingAutomations ||
      isCreatingAutomationTrigger,
  };
}
